import React from 'react';
import {
  Create, SimpleForm, TextInput, required, minLength, maxLength, useNotify, useRedirect,
} from 'react-admin';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { getChainId } from '../../lib';
import BolaoImageInput from './BolaoImageInput';

const Section: React.FC<{ title: string; children: React.ReactNode; hint?: string }> = ({ title, children, hint }) => (
  <Card sx={{ mb: 2, width: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: hint ? 0 : 2 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {hint}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const BolaoCreate: React.FC = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const chainId = getChainId();

  const transform = (data: any) => ({
    ...data,
    chainId,
  });

  const onSuccess = (data: any) => {
    notify('Bolão criado em rascunho. Configure os Gifts e ative quando estiver pronto.', { type: 'success' });
    redirect('show', 'boloes', data.id);
  };

  return (
    <Create
      {...props}
      resource="boloes"
      transform={transform}
      mutationOptions={{ onSuccess }}
      title="Criar bolão"
    >
      <SimpleForm sx={{ maxWidth: 920 }}>
        <Section title="Identificação" hint="Use um nome curto e claro. Exemplo: 'Brasil x Argentina — Final Copa 2026'.">
          <TextInput
            source="name"
            label="Nome do bolão"
            validate={[required(), minLength(3), maxLength(120)]}
            fullWidth
            helperText={false}
          />
          <TextInput
            source="description"
            label="Descrição (opcional)"
            multiline
            rows={3}
            fullWidth
            helperText={false}
          />
          <BolaoImageInput
            source="imageUrl"
            label="Banner (opcional)"
            kind="banner"
            helperText="Imagem larga exibida no topo da tela do bolão no app. JPG/PNG/WebP até 5MB."
          />
        </Section>

        <Section title="Partida" hint="Times e horários do jogo. Use horário de Brasília.">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                source="homeTeamName"
                label="Time da casa"
                validate={[required(), maxLength(80)]}
                fullWidth
                helperText={false}
              />
              <BolaoImageInput
                source="homeTeamLogo"
                label="Logo time da casa (opcional)"
                kind="home"
                helperText="Imagem quadrada (logo/escudo)"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                source="awayTeamName"
                label="Time visitante"
                validate={[required(), maxLength(80)]}
                fullWidth
                helperText={false}
              />
              <BolaoImageInput
                source="awayTeamLogo"
                label="Logo time visitante (opcional)"
                kind="away"
                helperText="Imagem quadrada (logo/escudo)"
              />
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', mt: 1 }}>
            <DateTimeInputUTC
              source="scheduledAt"
              label="Início do jogo"
              validate={[required()]}
              sx={{ flex: 1 }}
              helperText={false}
            />
            <DateTimeInputUTC
              source="guessDeadlineAt"
              label="Limite para palpitar"
              validate={[required()]}
              sx={{ flex: 1 }}
              helperText="Deve ser igual ou anterior ao kickoff"
            />
          </Stack>
        </Section>
      </SimpleForm>
    </Create>
  );
};

export default BolaoCreate;
